<template>
  <BaseModal
    :heading="$t('integrations.fiskaly_fr.heading')"
    grey
    :parentRoute="parentRouteName"
    useForm
    @submit="submit"
  >
    <Flex
      direction="column"
      :class="[$style.base, {[$style.smallScreen]: $screen === 's'}]"
    >
      <BaseAlert
        color="info"
        :text="$t('integrations.fiskaly_fr.description')"
        :primaryAction="{
          text: $t('global.learn_more'),
          href: helpCenterUrl
        }"
        v-test="'fiskaly-info-alert'"
      />
      <BaseSwitch
        v-model="formData.type"
        :options="[{ value: TaxpayerType.Company, label: $t('integrations.fiskaly_fr.types.legal') }, { value: TaxpayerType.Individual, label: $t('integrations.fiskaly_fr.types.person') }]"
        v-test="'fiskaly-type'"
      />
      <BaseCard
        v-if="formData.type === TaxpayerType.Company"
        :heading="$t('integrations.fiskaly_fr.company_details')"
      >
        <div :class="$style.formRow">
          <BaseInput
            v-model="formData.tradeName"
            :label="$t('integrations.fiskaly_fr.trade_name')"
            required
            :placeholder="$t('integrations.fiskaly_fr.trade_name_placeholder')"
            v-test="'fiskaly-trade-name'"
          />
          <BaseInput
            v-model="formData.legalName"
            :label="$t('integrations.fiskaly_fr.legal_name')"
            required
            :placeholder="$t('integrations.fiskaly_fr.legal_name_placeholder')"
            v-test="'fiskaly-legal-name'"
          />
        </div>
      </BaseCard>
      <BaseCard
        v-else
        :heading="$t('integrations.fiskaly_fr.personal_details')"
      >
        <Flex direction="column">
          <div :class="$style.formRow">
            <div :class="$style.formCellSmall">
              <BaseDropdown
                v-model="formData.gender"
                :options="[
                  { value: PersonGender.Male, label: 'Mr' },
                  { value: PersonGender.Female, label: 'Mrs' },
                  { value: PersonGender.Diverse, label: 'Diverse' },
                ]"
                :label="$t('integrations.fiskaly_fr.gender')"
                required
                v-test="'fiskaly-gender'"
              />
            </div>
            <BaseInput
              v-model="formData.firstName"
              :label="$t('integrations.fiskaly_fr.first_name')"
              required
              v-test="'fiskaly-first-name'"
            />
            <BaseInput
              v-model="formData.lastName"
              :label="$t('integrations.fiskaly_fr.last_name')"
              required
              v-test="'fiskaly-last-name'"
            />
          </div>
          <BaseInput
            v-model="formData.legalName"
            :label="$t('integrations.fiskaly_fr.legal_name_company')"
            required
            :placeholder="$t('integrations.fiskaly_fr.legal_name_placeholder')"
            v-test="'fiskaly-legal-name-company'"
          />
        </Flex>
      </BaseCard>
      <BaseCard :heading="$t('integrations.fiskaly_fr.fiscal_information')">
        <Flex direction="column">
          <div :class="$style.formRow">
            <BaseInput
              v-model="formData.siren"
              :label="$t('integrations.fiskaly_fr.siren_number')"
              required
              :placeholder="$t('integrations.fiskaly_fr.siren_number_placeholder')"
              :maxLength="9"
              :minLength="9"
              :regex="FR_SIREN_REGEX"
              v-test="'fiskaly-siren-number'"
            />
            <BaseInput
              v-model="formData.vatNumber"
              :label="$t('integrations.fiskaly_fr.vat_number')"
              :info="$t('integrations.fiskaly_fr.vat_number_info')"
              :placeholder="$t('integrations.fiskaly_fr.vat_number_placeholder')"
              :maxLength="13"
              :minLength="13"
              :regex="FR_VAT_NUMBER_REGEX"
              v-test="'fiskaly-vat-number'"
            />
          </div>
          <div :class="$style.formRow">
            <BaseDropdown
              v-model="formData.startDay"
              :options="dayOptions"
              :label="$t('integrations.fiskaly_fr.fiscal_year_start_day')"
              required
              v-test="'fiskaly-fiscal-year-start-day'"
            />
            <BaseDropdown
              v-model="formData.startMonth"
              :options="monthOptions"
              :label="$t('integrations.fiskaly_fr.fiscal_year_start_month')"
              required
              v-test="'fiskaly-fiscal-year-start-month'"
            />
          </div>
          <div :class="$style.formRow">
            <Flex
              direction="column"
              :gap=".5"
            >
              <BaseInput
                v-model="nafCode"
                :label="$t('integrations.fiskaly_fr.naf_code')"
                :info="$t('integrations.fiskaly_fr.naf_code_info')"
                :placeholder="$t('integrations.fiskaly_fr.naf_code_placeholder')"
                required
                :regex="NAF_CODE_REGEX"
                v-test="'fiskaly-naf-code'"
              />
              <BaseText
                href="https://www.insee.fr/fr/information/2406147"
                size="s"
                v-test="'fiskaly-naf-code-link'"
              >
                {{ $t('integrations.fiskaly_fr.naf_code_link_text') }}
              </BaseText>
            </Flex>
            <BaseDropdown
              v-model="formData.vatPayment"
              :options="[
                {
                  value: VatPaymentOption.Debits,
                  label: $t('integrations.fiskaly_fr.debits'),
                },
                {
                  value: VatPaymentOption.Receipts,
                  label: $t('integrations.fiskaly_fr.receipts'),
                },
              ]"
              :info="$t('integrations.fiskaly_fr.vat_payment_option_info')"
              :label="$t('integrations.fiskaly_fr.vat_payment_option')"
              required
              v-test="'fiskaly-vat-payment-option'"
            />
            <BaseDropdown
              v-model="formData.legalForm"
              :options="[
                {
                  value: 'EI',
                  label: 'EI',
                },
                {
                  value: 'EURL',
                  label: 'EURL',
                },
                {
                  value: 'SARL',
                  label: 'SARL',
                },
                {
                  value: 'SASU',
                  label: 'SASU',
                },
                {
                  value: 'SAS',
                  label: 'SAS',
                },
                {
                  value: 'SA',
                  label: 'SA',
                },
                {
                  value: 'SNC',
                  label: 'SNC',
                },
                {
                  value: 'SCOP',
                  label: 'SCOP',
                },
              ]"
              :label="$t('integrations.fiskaly_fr.legal_form')"
              :info="$t('integrations.fiskaly_fr.legal_form_info')"
              required
              v-test="'fiskaly-legal-form'"
            />
          </div>
          <div :class="$style.formRow">
            <BaseInput
              v-model="formData.rcsCity"
              :label="$t('integrations.fiskaly_fr.rcs_city')"
              required
              v-test="'fiskaly-rcs-city'"
            />
            <BaseInput
              v-model="formData.shareCapital"
              :label="$t('integrations.fiskaly_fr.share_capital')"
              :info="$t('integrations.fiskaly_fr.share_capital_info')"
              type="currency"
              :minValue="0"
              v-test="'fiskaly-share-capital'"
            />
          </div>
        </Flex>
      </BaseCard>
      <BaseCard
        :heading="$t('integrations.fiskaly_fr.registered_company_address')"
        v-test="'fiskaly-location-main'"
      >
        <Flex direction="column">
          <Address v-model="firstLocation.address" />
          <BaseInput
            v-model="firstLocation.label"
            :label="$t('integrations.fiskaly_fr.siret_number')"
            required
            :maxLength="14"
            :minLength="14"
            :regex="siretRegex"
            type="number"
            :placeholder="$t('integrations.fiskaly_fr.siret_number_placeholder')"
            v-test="'fiskaly-siret-number'"
            :info="$t('integrations.fiskaly_fr.siret_number_info')"
          />
        </Flex>
      </BaseCard>
      <BaseCard
        v-if="otherLocations.length"
        :heading="$t('integrations.fiskaly_fr.other_locations')"
      >
        <Flex direction="column">
          <Location
            v-for="(location, index) in otherLocations"
            :key="index"
            :location="location"
            :siretRegex="siretRegex"
            @updateAddress="(sameAddress) => updateAddress(sameAddress, index)"
          />
        </Flex>
      </BaseCard>
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        :routerLink="{ name: parentRouteName }"
        v-test="'fiskaly-cancel'"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :loading="provisionSignIntegrationLoading"
        v-test="'fiskaly-submit'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { getMonthNames } from '@/helpers/dates';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import {
  FR_SIREN_REGEX,
  FR_VAT_NUMBER_REGEX,
  NAF_CODE_REGEX,
} from '@/helpers/regex';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useUserStore } from '@/stores/user';
import type {
  LocationProvisionAttributes,
  ProvisionSignIntegrationInput,
} from '@/types';
import {
  AddressLineType,
  Country,
  FiscalizationCredentialsType,
  FiscalizationType,
  PersonGender,
  TaxpayerType,
  VatPaymentOption,
} from '@/types';
import Address from './Address.vue';
import { PROVISION_SIGN_INTEGRATION } from './graphql';
import Location from './Location.vue';

const props = defineProps<{
  parentRouteName: string;
}>();

const emit = defineEmits(['updated']);

const formData = reactive({
  type: TaxpayerType.Company,
  tradeName: '',
  legalName: '',
  firstName: '',
  lastName: '',
  siren: '',
  vatNumber: '',
  startDay: 1,
  startMonth: 1,
  nafCode: '',
  vatPayment: VatPaymentOption.Receipts,
  legalForm: '',
  rcsCity: '',
  shareCapital: 0,
  gender: null,
});

const { locations } = useLocationsStore();
const getDefaultAddress = () => ({
  city: '',
  country: Country.Fr,
  lineType: AddressLineType.StreetNumber,
  number: '',
  postalCode: '',
  street: '',
});

const locationData = ref<LocationProvisionAttributes[]>(
  locations.map((location) => ({
    address: getDefaultAddress(),
    label: '',
    locationId: location.id,
    name: location.internalName,
  })),
);

const firstLocation = computed(() => locationData.value[0]);
const otherLocations = computed(() => locationData.value.slice(1));

// NAF code = 2 digits + 2 digits + letter (e.g. "93.23Z"). A user might type it
// without the dot, so insert it automatically regardless of what they typed.
const nafCode = computed({
  get: () => formData.nafCode,
  set: (value: string) => {
    const clean = value.toUpperCase();
    const digits = clean.replace(/[^0-9]/g, '').slice(0, 4);
    const letter = clean.replace(/[^A-Z]/g, '').slice(0, 1);
    const formattedDigits =
      digits.length <= 2 ? digits : `${digits.slice(0, 2)}.${digits.slice(2)}`;
    formData.nafCode = `${formattedDigits}${letter}`;
  },
});

// SIRET = the SIREN + 5 more digits, so it must start with the SIREN typed
// above. Keep only digits so free-text input can't inject regex
// metacharacters into the RegExp.
const siretRegex = computed(() => {
  const siren = formData.siren.replace(/\D/g, '');
  return new RegExp(`^${siren}.{${14 - siren.length}}$`);
});

const updateAddress = (sameAddress: boolean, index: number) => {
  locationData.value[index + 1].address = sameAddress
    ? firstLocation.value.address
    : getDefaultAddress();
};

const monthOptions = computed(() => {
  return getMonthNames().map((name, index) => ({
    value: index + 1,
    label: name,
  }));
});

const dayOptions = computed(() => {
  return Array.from({ length: 31 }, (_, index) => ({
    value: index + 1,
    label: index + 1,
  }));
});

const { t } = useI18n();
const router = useRouter();

const {
  mutate: provisionSignIntegration,
  loading: provisionSignIntegrationLoading,
} = useMutation(PROVISION_SIGN_INTEGRATION);

const submit = () => {
  const { company } = useCompanyStore();

  const payload: ProvisionSignIntegrationInput = {
    locations: locationData.value.map((location) => ({
      ...location,
      label: location.label.toString(),
    })),
    organization: {
      name: company.name,
    },
    taxpayer: {
      address: firstLocation.value.address,
      fiscalization: {
        credentialsProperties: {
          legalForm: formData.legalForm,
          nafApeCode: formData.nafCode,
          rcsCity: formData.rcsCity,
          // The currency field binds minor units (cents); the backend converts
          // to a euros string. A cleared field coerces to 0, which the backend
          // treats as "no declared capital", so omit it here rather than send 0.
          socialCapital: formData.shareCapital
            ? String(formData.shareCapital)
            : undefined,
          vatPaymentOption: formData.vatPayment as unknown as VatPaymentOption,
        },
        credentialsType: FiscalizationCredentialsType.Lne,
        fiscalYearDate: `${String(formData.startMonth).padStart(2, '0')}-${String(formData.startDay).padStart(2, '0')}`,
        siren: formData.siren,
        type: FiscalizationType.Fr,
        // VAT-exempt companies (franchise en base) have none — omit, don't send ''.
        vatNumber: formData.vatNumber || undefined,
      },
      legalName: formData.legalName,
      tradeName: formData.tradeName,
      type: formData.type,
    },
  };

  if (formData.type === TaxpayerType.Individual) {
    payload.taxpayer.person = {
      firstName: formData.firstName,
      gender: formData.gender as unknown as PersonGender,
      lastName: formData.lastName,
    };
  }

  const userStore = useUserStore();

  provisionSignIntegration({ input: payload }).then(() => {
    flash(t('integrations.fiskaly_fr.success_message'));
    emit('updated');
    router.push({ name: props.parentRouteName });
    userStore.fetchOrCreateSystem();
  });
};

const helpCenterUrl = computed(() => helpCenterArticleUrl(16050328));
</script>

<style
  lang="scss"
  module
>
.formRow {
  display: flex;
  gap: $spacing;

  .base:not(.smallScreen) & {
    justify-content: space-between;

    & > * {
      flex: 1;
    }
  }

  .base.smallScreen & {
    flex-direction: column;
  }
}

.formCellSmall {
  .base:not(.smallScreen) & {
    flex: 0.5;
  }
}
</style>
